import React, { useCallback, useContext, useState } from 'react';
import { DocumentCarouselContext, IActionCriteria, IDocumentPrint } from '../../components/document/DocumentProvider';
import { useQuery } from '@apollo/client';
import {
  ISalesOrder,
  ISalesOrderLabelDocumentCarouselQueryResult,
  ISalesOrderLabelDocumentCarouselQueryVariables,
  ISalesOrderLabelGenerateRangeMutationResult,
  ISalesOrderLabelGenerateRangeMutationVariables,
  ReportType,
} from '../../graphql/schema/p4types';
import SalesOrderLabel from '../../graphql/entities/sales/SalesOrderLabel';
import {
  getDocumentCarouselReportParameters,
  IDocumentValues,
  useActionFilters,
  useDocumentCarouselDefaults,
  useSalesOrderDeliveryRecipient,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm } from 'react-hook-form';
import DocumentCarouselTemplate from '../../components/document/DocumentCarouselTemplate';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useDocumentCarouselComplete, useDocumentJobCompletion } from '../../utils/DocumentUtils';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentCarouselEmail from '../../components/document/DocumentCarouselEmail';
import SalesOrderLabelDocumentCarouselQuery from '../../graphql/queries/document/SalesOrderLabelDocumentCarouselQuery.graphql';
import DocumentCarouselPrintOptions from '../../components/document/DocumentCarouselPrintOptions';
import DocumentCarouselSummary from '../../components/document/DocumentCarouselSummary';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import FluentNumericInput from '../../components/FluentWrapper/FluentNumericInput';

//Note - DocumentModal2 currently uses 'documentSummary' instead of 'DOCUMENT_SUMMARY' - Possible cause for minor friction in transition.
export const DOCUMENT_SUMMARY = 'DOCUMENT_SUMMARY';
export const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';

/**
 * Render the content for the Picking Instruction Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const SalesOrderLabelDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
  documentPrint,
}: {
  documentDefaults: IDocumentValues;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: ISalesOrder;
  setCookie: ReturnType<typeof useCookies>[1];
  documentPrint: IDocumentPrint;
}) => {
  const { onSkip } = useContext(DocumentCarouselContext);
  const { onPrintComplete } = documentPrint;
  const [numPackages, setNumPackages] = useState<number>(1);

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues, watch } = methods;

  const onDocumentComplete = useDocumentCarouselComplete({
    watch,
    entity: SalesOrderLabel,
  });

  const onJobStart = useCallback(() => {
    onSkip(true);
  }, [onSkip]);

  const onJobComplete = useDocumentJobCompletion({
    evictionFieldNames: ['salesOrder'],
    onDocumentComplete,
    onPrintComplete,
  });

  const [salesOrderLabelPrintRange] = useBackgroundMutation<
    ISalesOrderLabelGenerateRangeMutationResult,
    ISalesOrderLabelGenerateRangeMutationVariables
  >(SalesOrderLabel.generateRange!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const onPrintClick = useCallback(() => {
    const reportParameters = getDocumentCarouselReportParameters({ getValues, reprint: false });

    salesOrderLabelPrintRange({
      variables: {
        input: {
          filters: actionCriteria.filters,
          salesOrders: normalizeVariables(actionCriteria.actionEntities),
          include: actionCriteria.include,
          clientMutationId: getClientMutationId(),
          reportParameters: reportParameters,
          numPackages: numPackages,
        },
      },
    });
  }, [
    actionCriteria.actionEntities,
    actionCriteria.filters,
    actionCriteria.include,
    getValues,
    numPackages,
    salesOrderLabelPrintRange,
  ]);

  const additionalRecipient = useSalesOrderDeliveryRecipient(singlePrintNode?.salesOrder ?? singlePrintNode);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={SalesOrderLabel}
        count={count}
        secondaryTitle={
          <DocumentCarouselHeaderInfo
            contentLeft={
              singlePrint
                ? `Sales Order ${singlePrintNode?.salesOrder?.orderNumber || singlePrintNode?.orderNumber}`
                : undefined
            }
            contentRight={
              singleRecipient
                ? `Customer ${
                    (singlePrintNode?.salesOrder?.customer?.name || singlePrintNode?.customer?.name) ?? 'Unknown'
                  }`
                : undefined
            }
          />
        }
        reprint={false}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselTemplate reportType={ReportType.SalesOrderLabel} singleRecipient={singleRecipient} />
        <DocumentCarouselOutput singleRecipient={singleRecipient} />
        <DocumentCarouselSummary singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselPrintOptions singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselEmail
          entity={SalesOrderLabel}
          recipient={singlePrintNode?.customer}
          singleRecipient={singleRecipient}
          emailOptions={{
            additionalRecipients: additionalRecipient ? [additionalRecipient] : [],
            allowEmailTo: true,
            allowEmailCc: true,
            allowSubject: true,
          }}
        />
        <FluentNumericInput
          type={'Number'}
          label="Number of Packages"
          value={numPackages}
          onChange={setNumPackages}
          min={1}
        />
      </div>
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param reprint - This is a reprint operation, will will use a different mutation and data queries.
 */
export const SalesOrderLabelDocumentCarouselCard = ({
  actionCriteria,
  documentPrint,
}: {
  actionCriteria: IActionCriteria;
  documentPrint: IDocumentPrint;
}) => {
  const filters = useActionFilters(actionCriteria);
  const { data, loading: salesOrderLoading } = useQuery<
    ISalesOrderLabelDocumentCarouselQueryResult,
    ISalesOrderLabelDocumentCarouselQueryVariables
  >(SalesOrderLabelDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['customer.id'],
        sumColumns: ['id'],
      },
    },
  });

  const loading = salesOrderLoading;

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Customer in the result, we can pull the document defaults off the Customer record and use it for the whole print.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = data?.listData?.aggregateRows?.length === 1;
  const singlePrintNode = data?.listData?.edges?.[0]?.node as Maybe<ISalesOrder>;

  const salesOrderLabelDefault = data?.documentConfig?.salesOrderLabelDefault;

  const [cookies, setCookie] = useCookies([DOCUMENT_SUMMARY, PRINT_INDIVIDUAL_PREFERENCE_COOKIE]);
  const documentDefaults = useDocumentCarouselDefaults({
    entity: SalesOrderLabel,
    commonDocumentDefault: salesOrderLabelDefault,
    singleRecipient,
    recipient: singlePrintNode?.customer,
    cookies,
    allowEntityEmail: false,
  });

  if (data && !loading) {
    return (
      <SalesOrderLabelDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={data?.listData?.count ?? 0}
        singleRecipient={singleRecipient}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
        documentPrint={documentPrint}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
